import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { Box, InputAdornment, TextField } from '@mui/material';
import { ActionButton, Dialog } from '@core/components';
import { useDebouncedValue } from '@core/hooks/useDebouncedValue';
import type { Design } from '../../../api/design';
import DesignGrid from '../../components/DesignGrid';
import type { VestmentType } from '../../enum/vestmentType';
import { useDesignPages } from '../../hooks/useDesignPages';

const SEARCH_DEBOUNCE_MS = 250;
const PRIORITY_CARD_COUNT = 8;
const BROWSE_COLUMNS = { xs: 2, sm: 3, md: 4, lg: 5 };

interface DesignBrowseDialogProps {
  onClose: () => void;
  vestmentType: VestmentType;
  initialSearch: string;
  selectedCode: string;
  onSelect: (design: Design) => void;
  onUpload?: (code: string) => void; // Omitted when designs can't be uploaded for the vestment type.
  onUseCode: (code: string) => void;
}

export function DesignBrowseDialog({
  onClose,
  vestmentType,
  initialSearch,
  selectedCode,
  onSelect,
  onUpload,
  onUseCode,
}: DesignBrowseDialogProps) {
  const [searchInput, setSearchInput] = useState(initialSearch);
  const typed = searchInput.trim();
  const search = useDebouncedValue(typed, SEARCH_DEBOUNCE_MS);
  const pages = useDesignPages({ vestmentType, search: search || undefined });
  const { designs, isPending, isError } = pages;

  const matchesDesign = designs.some(
    (design) => design.code.toLowerCase() === typed.toLowerCase()
  );
  const unmatchedCode =
    !isPending && search === typed && typed && !matchesDesign ? typed : '';

  const emptyMessage = isError
    ? 'Failed to load designs.'
    : search
      ? 'No designs match your search.'
      : 'No designs for this vestment yet.';

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      title={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            Choose a design
            {onUpload ? (
              <ActionButton
                variant="outline"
                size="sm"
                icon={<AddIcon />}
                onClick={() => onUpload(unmatchedCode)}
              >
                Upload new design
              </ActionButton>
            ) : null}
          </Box>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search by code"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      }
      cancelText="Close"
      hideSubmit={!unmatchedCode}
      submitText={`Use "${unmatchedCode}" as code`}
      onSubmitClick={() => onUseCode(unmatchedCode)}
    >
      <DesignGrid
        pages={pages}
        columns={BROWSE_COLUMNS}
        priorityCount={PRIORITY_CARD_COUNT}
        emptyMessage={emptyMessage}
        minHeight="50vh"
        selectedCode={selectedCode}
        compact
        onOpen={onSelect}
      />
    </Dialog>
  );
}
